import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PEOOrganizationService } from '@/services/peo-organization.service';
import { useToast } from '@/hooks/use-toast';
import { EnhancedPEORepresentative } from '@/types/peo-organization.types';

interface TeamMemberDialogProps {
  providerId: string;
  member?: EnhancedPEORepresentative;
  children: React.ReactNode;
}

export const TeamMemberDialog: React.FC<TeamMemberDialogProps> = ({
  providerId,
  member,
  children
}) => {
  const [open, setOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [department, setDepartment] = useState('');
  const [region, setRegion] = useState('');
  const [territory, setTerritory] = useState('');
  const [managerUserId, setManagerUserId] = useState<string>('');

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get potential managers (other verified representatives)
  const { data: representatives } = useQuery({
    queryKey: ['enhanced-representatives', providerId],
    queryFn: () => PEOOrganizationService.getEnhancedRepresentatives(providerId),
    enabled: open && !!providerId
  });

  // Initialize form with existing member data
  useEffect(() => {
    if (member) {
      setTeamName(member.team_name || '');
      setDepartment(member.department || '');
      setRegion(member.region || '');
      setTerritory(member.territory?.join(', ') || '');
      setManagerUserId(member.manager_user_id || '');
    } else {
      // Reset form for new member
      setTeamName('');
      setDepartment('');
      setRegion('');
      setTerritory('');
      setManagerUserId('');
    }
  }, [member, open]);

  const updateTeamInfo = useMutation({
    mutationFn: async (data: {
      userId: string;
      teamInfo: {
        team_name?: string;
        department?: string;
        region?: string;
        territory?: string[];
      };
      managerUserId?: string | null;
    }) => {
      await PEOOrganizationService.updateRepresentativeTeamInfo(
        data.userId,
        data.teamInfo
      );
      
      if (data.managerUserId !== undefined) {
        await PEOOrganizationService.updateRepresentativeManager(
          data.userId,
          data.managerUserId
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enhanced-representatives', providerId] });
      queryClient.invalidateQueries({ queryKey: ['org-chart-nodes', providerId] });
      toast({
        title: 'Success',
        description: `Team member ${member ? 'updated' : 'configured'} successfully`,
      });
      setOpen(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to ${member ? 'update' : 'configure'} team member: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = () => {
    if (!member) return;

    const territoryArray = territory 
      ? territory.split(',').map(t => t.trim()).filter(Boolean)
      : [];

    updateTeamInfo.mutate({
      userId: member.user_id,
      teamInfo: {
        team_name: teamName || undefined,
        department: department || undefined,
        region: region || undefined,
        territory: territoryArray.length > 0 ? territoryArray : undefined,
      },
      managerUserId: managerUserId && managerUserId !== 'none' ? managerUserId : null,
    });
  };

  // Filter out the current member from potential managers
  const potentialManagers = representatives?.filter(rep => 
    rep.user_id !== member?.user_id
  ) || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {member ? `Edit ${member.profile?.first_name} ${member.profile?.last_name}` : 'Add Team Member'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {member && (
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium">
                    {member.profile?.first_name?.[0]}{member.profile?.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium">{member.profile?.first_name} {member.profile?.last_name}</h4>
                  <p className="text-sm text-muted-foreground">{member.profile?.email}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="e.g., Sales Team Alpha"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g., Sales, Marketing, HR"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="region">Region</Label>
              <Input
                id="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g., Northeast, West Coast"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="manager">Manager</Label>
              <Select value={managerUserId} onValueChange={setManagerUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {potentialManagers.map((rep) => (
                    <SelectItem key={rep.user_id} value={rep.user_id}>
                      {rep.profile?.first_name} {rep.profile?.last_name} ({rep.organizational_role?.role_name || 'No Role'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="territory">Territory</Label>
            <Textarea
              id="territory"
              value={territory}
              onChange={(e) => setTerritory(e.target.value)}
              placeholder="Enter territories separated by commas (e.g., California, Nevada, Arizona)"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={updateTeamInfo.isPending || !member}
          >
            {updateTeamInfo.isPending ? 'Saving...' : member ? 'Update Member' : 'Add Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};